import React from 'react';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
}

const Input: React.FC<InputProps> = ({
  label,
  error,
  helperText,
  leftIcon,
  rightIcon,
  fullWidth = true,
  className = '',
  id,
  ...props
}) => {
  const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;
  
  const baseStyles = `
    block w-full px-4 py-3
    border rounded-xl
    transition-all duration-200 ease-out
    focus:outline-none focus:ring-2 focus:ring-offset-0
    placeholder:text-gray-400
    disabled:bg-gray-50 disabled:cursor-not-allowed
    text-base sm:text-sm
    min-h-[44px]
  `;
  const normalStyles = `
    border-gray-300 bg-white text-gray-900
    hover:border-gray-400
    focus:ring-[#3B82F6] focus:border-[#3B82F6]
    focus:shadow-sm focus:shadow-blue-500/10
  `;
  const errorStyles = `
    border-red-500 bg-red-50/50 text-red-900
    placeholder:text-red-300
    focus:ring-red-500 focus:border-red-500
    focus:shadow-sm focus:shadow-red-500/10
  `;
  const iconPadding = leftIcon ? 'pl-11' : rightIcon ? 'pr-11' : '';
  
  return (
    <div className={fullWidth ? 'w-full' : ''}>
      {label && (
        <label 
          htmlFor={inputId} 
          className="block text-sm font-semibold text-gray-700 mb-2"
        >
          {label}
        </label>
      )}
      <div className="relative">
        {leftIcon && (
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
            <span className="text-lg">{leftIcon}</span>
          </div>
        )}
        <input
          id={inputId}
          className={`
            ${baseStyles}
            ${error ? errorStyles : normalStyles}
            ${iconPadding}
            ${className}
          `.replace(/\s+/g, ' ').trim()}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={error ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined}
          {...props}
        />
        {rightIcon && (
          <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none text-gray-400">
            <span className="text-lg">{rightIcon}</span>
          </div>
        )}
      </div>
      {error && (
        <p 
          id={`${inputId}-error`} 
          className="mt-2 text-sm text-red-600 font-medium flex items-center gap-1.5 animate-in fade-in slide-in-from-top-1 duration-200" 
          role="alert"
        >
          <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          {error}
        </p>
      )}
      {helperText && !error && (
        <p 
          id={`${inputId}-helper`} 
          className="mt-2 text-sm text-gray-500"
        >
          {helperText}
        </p>
      )}
    </div>
  );
};

export default Input;

